import React from 'react';
import PropTypes from "prop-types";
import classnames from 'classnames';
import { DragSource } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import LazyLoad from 'react-lazy-load';
import Checkbox from "@components/common/checkbox/main";
import FontClass from "@components/asset/shared/fontClass";
import oldType from '@helpers/oldType';
import isOutOfViewport from '@helpers/isInViewport';
import { Collection, Resource } from '@helpers/show_page_helpers';
import downloadAsset from '@api/v3/assets/download';
import ActionsMenu from './actions_menu';

import './styles/card.scss';

class Card extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      menuDirection: 'right',
      renderCardActions: false,
    };

    this.container = React.createRef();
  }

  componentDidMount() {
    if (this.props.reorderable) {
      this.props.connectDragPreview(getEmptyImage(), {
        captureDraggingState: true,
      });
    }
  }

  handleThumbnailError = (e) => {
    // Hacky, but don't want to implement state unless necessary
    const { asset } = this.props;
    const { extension, name } = asset.attributes;
    const modifiedExtension = extension ? extension.replace('.', '') : '-';
    const source = (modifiedExtension) ? `//static-fast.brandfolder.com/assets/types/${modifiedExtension}.svg` : `//static-fast.brandfolder.com/assets/types/file.svg`;
    e.target.src = source;
    e.target.alt = name;
    e.target.style.cssText = "padding: 25px;";
  }

  createAndClickLink() {
    const { asset } = this.props;
    const { asset_data, printui_editor_link } = asset.attributes;

    const link = document.createElement("a");
    link.id = `press-link-${asset.id}`; // give it an ID!
    link.href = printui_editor_link
      ? `${printui_editor_link}${BF_Manifest ? `&digest=${BF_Manifest}` : ""}`
      : asset_data.url;
    link.target = "_blank";

    document.body.appendChild(link);
    const domLink = document.getElementById(link.id);
    domLink.click();
    // Need this because IE11 doesn't support javascript .remove()
    if (!('remove' in Element.prototype)) {
      Element.prototype.remove = function () {
        if (this.parentNode) {
          this.parentNode.removeChild(this);
        }
      };
    }
    document.getElementById(link.id).remove();
  }

  buttonHoverText() {
    const { asset } = this.props;
    if (asset.attributes.type === "Press" || asset.type === "Press" || asset.attributes.extension === "bftemplate") {
      return "Visit";
    }

    if (BFG.embedView && (asset.attributes.type === "texts" || asset.type === "texts")) {
      // Bubbles up to asset/card/main postEmbedMessageText function
      return "Select";
    }

    return "View";
  }

  downloadAsset() {
    const { asset } = this.props;
    const $downloadIndicator = $('.download_progress_indicator');
    const collectionKey = (BF.fx.collection() && BF.fx.collection().key) ? BF.fx.collection().key : "";
    $downloadIndicator.css("visibility", "visible").addClass('active');
    const url = `/api/v3/assets/${asset.id}/download`;
    downloadAsset(asset.id, { collectionKey, manifest: BF_Manifest }).then((response) => {
      if (response.data && !!response.data.redirect_url) {
        window.location.href = response.data.redirect_url;
        $downloadIndicator.css("visibility", "hidden");
        Notify.create({ title: `Downloaded ${response.data.attachment_count} ${BF.fx.should_pluralize(response.data.attachment_count, 'file')}`, type: "success" });
        Insight.createEvent('downloaded', asset.id, 'asset');
      } else {
        BF.BulkAction.result_poller(url.replace('download', 'bulk-convert-download-poller'), response.data, 500);
      }
    }).catch(() => {
      Notify.create({ title: "Something went wrong. Try again soon!", type: "error" });
      $downloadIndicator.css("visibility", "hidden");
    });
  }

  renderAssetThumbnail() {
    const { asset } = this.props;
    const { thumbnail_url, name, background_color, availability } = asset.attributes;
    let { type } = asset.attributes;
    const style = background_color ? { backgroundColor: background_color, background: background_color } : { backgroundColor: "transparent" };
    if (this.props.oldTypes) {
      type = asset.type;
    }

    let colorHtml;
    switch (type) {
      case "Color":
      case "colors":
        if (asset.attributes) {
          colorHtml = asset.attributes.html;
        }
        if (colorHtml) {
          return (
            <div className="card-thumbnail" style={{ backgroundColor: colorHtml, background: colorHtml }}>
              {availability !== "available" ? this.renderBanner() : ""}
            </div>
          );
        }

        return (
          <div className="card-thumbnail">
            {availability !== "available" ? this.renderBanner() : ""}
          </div>
        );
      case "Font":
      case "fonts":
        return (
          <div className="card-thumbnail">
            {availability !== "available" ? this.renderBanner() : ""}
            <FontClass asset={asset} showPage>Aa Bb </FontClass>
          </div>
        );
      case "Text":
      case "texts":
        return (
          <div className="card-thumbnail" style={{ background: 'none', display: 'block' }}>
            {availability !== "available" ? this.renderBanner() : ""}
            <h4 className="asset-name">{name}</h4>
            {this.renderDescription()}
          </div>
        );
      default:
        return (
          <div className="card-thumbnail" style={style}>
            {availability !== "available" ? this.renderBanner() : ""}
            <LazyLoad height="100%" offsetVertical={5000}>
              <img
                className={`image-div ${(thumbnail_url || '').toString().includes("/assets/types") ? "thumbnail_fallback" : ""}`}
                src={thumbnail_url}
                alt={name}
                onError={(e) => {
                  if (typeof this.handleThumbnailError === "function") {
                    this.handleThumbnailError(e);
                  }
                  this.handleThumbnailError = null;
                }}
              />
            </LazyLoad>
          </div>
        );
    }
  }

  renderBanner() {
    switch (this.props.asset.attributes.availability) {
      case "pending_approval":
        return <div className="s-unapproved-ribbon"> Pending </div>;
      case "unpublished":
        return <div className="s-unapproved-ribbon"> Draft </div>;
      case "expired":
        return <div className="s-unapproved-ribbon"> Expired </div>;
      default:
        return undefined;
    }
  }

  renderDescription() {
    const { asset } = this.props;
    const { description } = asset.attributes;
    const formatted_description = description.replace(/\n/g, "<br>");
    // USE THIS IF YOU WANT ABBREVIATED TEXT
    // let descriptionText = description && description.length > 770 ? description.substr(0, 770) + '...' : description;
    // let div = document.createElement("div");
    // div.innerHTML = descriptionText;
    // let text = div.textContent || div.innerText || "";
    //
    // return (
    //   <div className="description">
    //     {text}
    //   </div>);

    // USE THIS IF YOU WANT TO PRINT THE HTML
    return (
      <div
        className="description"
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        dangerouslySetInnerHTML={{ __html: formatted_description }}
      />
    );
  }

  renderActions() {
    const { menuDirection, renderCardActions } = this.state;

    if (!renderCardActions) { return null; }

    const {
      editable,
      downloadable,
      asset,
      section,
      reorderable,
      activeLabel,
      connectDragSource,
    } = this.props;

    const viewOnly = (Resource.type === 'collection') && Collection.view_only;
    const canShare = BFG.context.hasFeature('share_links') && BFG.manifestDigest === '' && (editable || !viewOnly);
    const canAnalyze = BFG.context.hasFeature('insights') && ['admin', 'owner'].includes(BF.fx.role());
    const downloadableSection = section && !['Color', 'Text'].includes(section.type);
    const downloadableAsset = !asset.attributes.printui_editor_link;

    return (
      <div className="asset-actions">
        <div className="resource-link">
          <div className="asset-hover-state">
            <button
              className="button secondary options-item"
              type="button"
            >
              {this.buttonHoverText()}
            </button>
          </div>
        </div>
        <div className="action-menu">
          <div className="left-side-actions">
            {reorderable ? connectDragSource(
              <button type="button">
                <span className="bff-reorder" aria-label="Reorder" />
              </button>
            ) : null}
          </div>
          <div className="right-side-actions">
            <div className="actions-menu__container">
              <button
                className="actions-menu__toggle"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onMouseEnter={() => {
                  const isObscured = isOutOfViewport(this.container.current, 300).right;
                  if (isObscured) {
                    this.setState({ menuDirection: 'left' });
                  }
                }}
              >
                <span className="bff-ellipses" aria-label="Show menu" />
              </button>
              <ActionsMenu
                asset={asset}
                section={section}
                canEdit={editable}
                canShare={canShare}
                canAnalyze={canAnalyze}
                canDelete={editable && !Collection.key}
                canRemove={editable && Boolean(Collection.key)}
                canUnlabel={editable && Boolean(activeLabel.key)}
                canDownload={downloadable && downloadableSection && downloadableAsset}
                openDirection={menuDirection}
                collectionId={Collection.key}
                label={activeLabel}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderAttachmentInfo() {
    const { attachment_count, extension } = this.props.asset.attributes;

    return (
      <span className="attachment-info">
        {attachment_count > 1 ? (
          <React.Fragment>
            <span className="bff-paperclip" />
            {attachment_count > 100 ? <React.Fragment> + </React.Fragment> : <React.Fragment> {attachment_count} </React.Fragment>}
          </React.Fragment>
        ) : (<React.Fragment>{extension === "brandfolder/template" || extension === "bftemplate" ? <i className="fa fa-object-ungroup" /> : extension}</React.Fragment>)}
      </span>
    );
  }

  // currently not rendered anywhere except print view
  // when custom fields feature is enabled
  renderCustomFields() {
    const { asset, list } = this.props;
    return (
      <div className={`custom-fields ${list ? 'list-view' : ''}`}>
        {Object.keys(asset.attributes.custom_fields).map((key) => (
          <div className="custom-fields__row">
            <h5 key={`${asset.id}${key}-1`} className="custom-fields__row--item">
              {key}:
            </h5>
            <h5 key={`${asset.id}${key}-2`} className="custom-fields__row--item">
              {asset.attributes.custom_fields[key]}
            </h5>
          </div>
        ))}
      </div>
    );
  }

  renderCard() {
    const { asset, enableContactSheets } = this.props;
    const { attachment_count, extension, name } = asset.attributes;
    let { type } = asset.attributes;
    const { viewAsset } = this.props;
    const hideAttachmentInfo = (extension === "empty" && attachment_count < 1) || type === "Text" || type === "texts";

    if (this.props.oldTypes) {
      type = asset.type;
    }
    return (
      <React.Fragment>
        <div
          id={asset.id}
          className="card asset-card j-resource-card"
          onClick={() => {
            if (type === "Press" || extension === "bftemplate") { this.createAndClickLink(); }
          }}
        >
          {this.renderAssetThumbnail()}
          {this.renderActions()}
        </div>
        <div className="description description-foot">
          <div className="flex-left">
            {BFG.embedView ? null : (
              <Checkbox
                size="md"
                checked={this.props.selected}
                toggle={this.props.handleSelectionClick}
              />
            )}
            {(type !== "Text" || type === "texts") ? (
              <button
                type="button"
                className="asset-name"
                onClick={(e) => {
                  if (type === "Press" || extension === "bftemplate") {
                    viewAsset(asset.id);
                  } else {
                    this.props.handleSelectionClick(e);
                  }
                }}
              >
                {name}
              </button>
            ) : ''}
          </div>
          {!hideAttachmentInfo ? this.renderAttachmentInfo() : ''}
        </div>
        {
          enableContactSheets && Object.keys(asset.attributes.custom_fields).length > 0
          && this.renderCustomFields()
        }
      </React.Fragment>
    );
  }

  renderTags() {
    const { tag_names } = this.props.asset.attributes;
    const totalTags = tag_names.length;

    return tag_names.sort().map((tag, i) => (
      <li key={`${tag}-${i}`}> {tag}{i < totalTags - 1 ? ", " : ''} </li>
    ));
  }

  renderList() {
    const {
      asset,
      editAsset,
      handleSelectionClick,
      selected,
      connectDragSource,
      reorderable,
    } = this.props;
    const {
      attachment_count,
      extension,
      name,
      printui_editor_link,
      tag_names,
      type
    } = asset.attributes;
    const hideAttachmentInfo = (extension === "empty" && attachment_count < 1) || type === "Text" || type === "texts";

    return (
      <React.Fragment>
        <div className="list-fixed-column">
          <div className="list-asset-checkbox">
            <Checkbox
              size="md"
              checked={selected}
              toggle={handleSelectionClick}
            />
          </div>
          <div
            id={asset.id}
            className="list-asset-card j-resource-card"
            onClick={(e) => {
              if (type === "Press" || extension === "bftemplate") {
                e.stopPropagation();
                this.createAndClickLink();
              }
            }}
          >
            {this.renderAssetThumbnail()}
          </div>
          <div className="list-asset-name asset-name asset-name-mobile">
            <p>{name}</p>
          </div>
        </div>
        <div className="list-flex-column">
          <div className="list-asset-name asset-name asset-name-desktop">
            <p>{name}</p>
          </div>
          <div className="list-asset-tags">
            {tag_names && tag_names.length > 0 ? <ul> {this.renderTags()} </ul> : ''}
          </div>

          <div className="list-asset-actions">
            {!hideAttachmentInfo ? this.renderAttachmentInfo() : ''}
            <div className="action-menu edit-button">
              <div
                className="j-edit-asset-action m-element-action-icon"
                onClick={(e) => { e.stopPropagation(); editAsset(); }}
              >
                <button className="button tertiary">
                  <span className="bff-edit-asset" aria-label="Edit" />
                  <span className="edit-button-text">Edit</span>
                </button>
              </div>
            </div>
            {(attachment_count > 0 && !printui_editor_link) ? (
              <button className="download-button primary button">
                <div className="m-element-action-icon">
                  <span className="bff-download-asset" aria-label="Download" onClick={(e) => { e.stopPropagation(); this.downloadAsset(); }} />
                  <span className="download-button-text">Download</span>
                </div>
              </button>
            ) : (
              <div className="placeholder-button" />
            )}
            {reorderable ? connectDragSource(
              <button className="asset-reorder" type="button">
                <span className="bff-reorder" aria-label="Reorder" />
              </button>
            ) : null}
          </div>
        </div>
      </React.Fragment>
    );
  }

  render() {
    const {
      asset,
      list,
      isHovered,
      insertCaretDirection,
      onVisitAsset,
      alwaysOpenInModal,
    } = this.props;
    const {
      availability,
      extension,
      type
    } = asset.attributes;

    let insertCaret = '';
    if (isHovered && insertCaretDirection) {
      insertCaret = `select-${insertCaretDirection || ''}`;
    }

    return (
      <div
        ref={this.container}
        className={classnames(
          'base-card',
          'print-card',
          availability,
          insertCaret,
          `asset-type-${oldType(type)}`,
          {
            list,
            "li-large": type === "Text",
          },
        )}
        onClick={() => {
          if (alwaysOpenInModal) {
            onVisitAsset(asset.id);
          } else if (!((type === "Press" || extension === "bftemplate") && !list)) {
            onVisitAsset(asset.id);
          }
        }}
        onMouseEnter={() => (this.setState({ renderCardActions: true }))}
      >
        {list ? this.renderList() : this.renderCard()}
      </div>
    );
  }
}

Card.propTypes = {
  asset: PropTypes.shape({}).isRequired,
  editable: PropTypes.bool.isRequired,
  downloadable: PropTypes.bool.isRequired,
  editAsset: PropTypes.func,
  handleSelectionClick: PropTypes.func.isRequired,
  list: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  viewAsset: PropTypes.func,
  section: PropTypes.shape({
    key: PropTypes.string,
    type: PropTypes.string,
  }),
  isHovered: PropTypes.bool,
  insertCaretDirection: PropTypes.oneOf(['', 'top', 'bottom', 'left', 'right']),
  onVisitAsset: PropTypes.func,
  reorderable: PropTypes.bool,
  alwaysOpenInModal: PropTypes.bool,
  activeLabel: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
  }),

  /* eslint-disable react/no-unused-prop-types */
  connectDragSource: PropTypes.func,
  connectDragPreview: PropTypes.func,
  onDragComplete: PropTypes.func,
  /* eslint-enable react/no-unused-prop-types */
  enableContactSheets: PropTypes.bool
};

Card.defaultProps = {
  onVisitAsset: () => {},
  isHovered: false,
  insertCaretDirection: '',
  reorderable: false,
  alwaysOpenInModal: false,
  activeLabel: null,
  connectDragSource: () => {},
  connectDragPreview: () => {},
  onDragComplete: () => {},
  enableContactSheets: false,
};

const dragSource = {
  beginDrag(props) {
    const { index, section, asset } = props;
    const { name, attachment_count: attachmentCount } = asset.attributes;

    return {
      index,
      id: asset.id,
      sectionId: section.key,
      name,
      attachmentCount,
      reorder: true,
    };
  },
  endDrag(props, monitor) {
    props.onDragComplete(monitor.getItem());
  },
};

const dragCollect = (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  isDragging: monitor.isDragging(),
  connectDragPreview: connect.dragPreview(),
});

export default DragSource('asset-reorder', dragSource, dragCollect)(Card);
export { Card as PlainCard };
